import { useState } from 'react';
import { Link } from 'react-router-dom';
import { NudosHoverText } from '../../../../components/NudosComponents';
import {
  IlogisticServiceTrelloCardData,
  LOCATION_PLACE_TYPE,
  LOGISTIC_SERVICE_STATUS_NAME
} from '../../../../types/services';
import { IconArrow } from '../../../../assets/DesignSystem/SVGComponents';
import LogisticsOriginAndDestination from './Components/LogisticsOriginAndDestination/LogisticsOriginAndDestination';
import LogisticServicePaymentInformation from './Components/LogisticServicePaymentInformation/LogisticServicePaymentInformation';
import LsTrelloCardUpperRightInformation from './Components/LogisticServiceDatesInformation/LsTrelloCardUpperRightInformation';
import ModalAddPaymentLink from '../ModalsLogisticsService/ModalAddPaymentLink/ModalAddPaymentLink';
import { InformationIcon } from '../../../../assets/images/shopping';
import { IconPlatinum } from '../../../../assets/images/services';
import BordResponsibleUserPictures from '../../../../components/NudosComponents/BordReponsibleUser/BordReponsibleUserPictures/BordResponsibleUserPictures';
import useUsersState from '../../../../state/useUsersState';
import './LogisticsServiceTrelloCard.scss';

const LogisticsServiceTrelloCard = ({
  logisticServiceData,
  openDelayedModalCallback,
  updateTrelloDataCallback,
  cardStatusColumnClassname
}: {
  logisticServiceData: IlogisticServiceTrelloCardData;
  updateTrelloDataCallback?: () => void;
  openDelayedModalCallback?: () => void;
  cardStatusColumnClassname?: string;
}) => {
  const { users } = useUsersState();

  const {
    id,
    logisticType,
    organizationName,
    levelSubscription,
    numberOfTools,
    hasPendingConfiguration,
    proofOfPaymentUrl,
    origin,
    destination,
    payments,
    status,
    documentUrl,
    quoteDocumentUrl,
    manualQuote,
    quotesLog,
    responsibleIds,
    addressIssue
  } = logisticServiceData;

  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [showQuoteTypeInfo, setShowQuoteTypeInfo] = useState(false);
  const [showAddressChange, setShowAddressChange] = useState(false);

  const rejectedQuote = quotesLog?.status === 'rejected';
  const isDelivered = status === LOGISTIC_SERVICE_STATUS_NAME.DELIVERED;
  const isToBeConfirmed = status === LOGISTIC_SERVICE_STATUS_NAME.TOCONFIRMED;
  const toolDestinationIsWarehouse = [LOCATION_PLACE_TYPE.NUDOS, LOCATION_PLACE_TYPE.DIAGNOSTIC].includes(
    (destination?.place || '') as LOCATION_PLACE_TYPE
  );
  const toolOriginIsNudos = origin?.place === LOCATION_PLACE_TYPE.NUDOS;
  const noDataText = 'Sin datos';
  const logisticServiceDetailRedirectionUrl = id ? `/logistic-service/details/${id}` : '/logistic-service/listing';
  const hasNudosLite = levelSubscription === 'Lite';
  const toolsText = numberOfTools && numberOfTools > 1 ? 'Herramientas' : 'Herramienta';
  const numberOfToolsText = typeof numberOfTools === 'number' ? `${numberOfTools} ${toolsText}` : noDataText;
  const doesntHaveDns = isDelivered && toolDestinationIsWarehouse && !documentUrl;
  const isDelayed = cardStatusColumnClassname === 'delayed' || cardStatusColumnClassname === 'delayedUpdated';

  const originViewedChanges = addressIssue?.origin?.viewedChanges;
  const destinationViewedChanges = addressIssue?.destination?.viewedChanges;
  const originAddressEditedByUser = logisticServiceData?.addressIssue?.origin?.editedBy === 'user';
  const destinationAddressEditedByUser = logisticServiceData?.addressIssue?.destination?.editedBy === 'user';

  const originAddressEditedByUserAndNotViewed = originAddressEditedByUser && !originViewedChanges;
  const destinationAddressEditedByUserAndNotViewed = destinationAddressEditedByUser && !destinationViewedChanges;
  const displayAddresChange = originAddressEditedByUserAndNotViewed || destinationAddressEditedByUserAndNotViewed;
  const addressChangeStyles = displayAddresChange ? 'displayAddresChange' : '';

  const assignmentWithPendingConfigurations = toolOriginIsNudos && !isToBeConfirmed && hasPendingConfiguration;
  const deliveredLsWithPendingConfigurations = isDelivered && hasPendingConfiguration;
  const pendingConfigurations = assignmentWithPendingConfigurations || deliveredLsWithPendingConfigurations;
  const showRedDeliveredCard = doesntHaveDns || deliveredLsWithPendingConfigurations;
  const redCardStyles = showRedDeliveredCard ? 'redCardStyles' : '';
  const isPaymentAgreementWithCreditBord =
    payments?.paymentMethod?.toLowerCase()?.includes('crédito bord') &&
    !!payments?.paymentAgreementDate &&
    (responsibleIds?.length === 0);
 
    const logisticsDetailsRedirectionData = {
    pathname: logisticServiceDetailRedirectionUrl,
    state: {
      logisticServiceTrelloData: logisticServiceData
    }
  };

  const addressChangeHoverText = (): string => {
    const originEditsLength = addressIssue?.origin?.editHistory?.length || 0;
    const destinationEditsLength = addressIssue?.destination?.editHistory?.length || 0;

    if (originEditsLength > 0 && destinationEditsLength > 0) {
      return 'Se actualizó la información de origen y destino de este servicio';
    }

    if (originEditsLength > 0) {
      const baseText = 'Se actualizó la información de origen de este servicio';
      return originEditsLength > 1 ? `${baseText} nuevamente` : baseText;
    }

    if (destinationEditsLength > 0) {
      const baseText = 'Se actualizó la información de destino de este servicio';
      return destinationEditsLength > 1 ? `${baseText} nuevamente` : baseText;
    }

    return 'Se actualizó la información de este servicio';
  };

  const lsManualData = {
    icon: '🤚',
    text: 'Cotización manual'
  };
  const lsAutomaticData = {
    icon: '🔁',
    text: 'Cotización automatica'
  };

  const specialNotifications = {
    missingDns: doesntHaveDns,
    isDelayed,
    pendingConfigurations
  };

  const responsibleUsersListFromState = users.filter(user => responsibleIds?.includes(user.id));

  const responsibleUsersList = responsibleUsersListFromState.map(user => ({
    id: user?.id,
    user: user,
    logisticServiceId: Number(id)
  }));

  const displayBordReponsibleUserPictures =
    status !== LOGISTIC_SERVICE_STATUS_NAME.TOCONFIRMED && !!responsibleUsersList?.length;

  const openPaymentLinkModal = () => setShowPaymentLinkModal(true);
  const closePaymentLinkModal = () => setShowPaymentLinkModal(false);

  return (
    <div
      className={`LogisticsServiceTrelloCard ${cardStatusColumnClassname || ''} ${
        rejectedQuote ? 'rejected' : ''
      } ${redCardStyles}`}
    >
      {showPaymentLinkModal && (
        <ModalAddPaymentLink
          closeModalCallback={closePaymentLinkModal}
          updateTrelloDataCallback={updateTrelloDataCallback}
          logisticServiceData={logisticServiceData}
        />
      )}
      <div className="trelloCardHeader">
        <div className="serviceType">
          <NudosHoverText
            customClassName="name"
            onlyIfTruncated
            text={organizationName || noDataText}
            charactersLimit={35}
          />
          {hasNudosLite && (
            <div className="platinumContainer">
              <IconPlatinum />
            </div>
          )}
        </div>
        <Link className={`serviceId ${addressChangeStyles}`} to={logisticsDetailsRedirectionData}>
          {displayAddresChange && (
            <div
              className="informationIconContainer"
              onMouseOver={() => setShowAddressChange(true)}
              onMouseLeave={() => setShowAddressChange(false)}
            >
              <InformationIcon className="informationIcon" />
              {showAddressChange && <div className="hoverText">{addressChangeHoverText()}</div>}
            </div>
          )}
          <div>SL Nº {id}</div>
          <IconArrow className="IconArrow" />
        </Link>
      </div>

      <div className="trelloCardBody">
        <div className="topSection">
          <div className="nameAndNudosLite">
            <NudosHoverText
              customClassName="name"
              onlyIfTruncated
              text={logisticType || noDataText}
              charactersLimit={20}
            />
            <div
              className="quoteStatus"
              onMouseOver={() => setShowQuoteTypeInfo(true)}
              onMouseLeave={() => setShowQuoteTypeInfo(false)}
            >
              {logisticServiceData?.manualQuote ? lsManualData?.icon : lsAutomaticData?.icon}
              {showQuoteTypeInfo && (
                <div className="hoverText">
                  {logisticServiceData?.manualQuote ? lsManualData?.text : lsAutomaticData?.text}
                </div>
              )}
            </div>
          </div>
          <LsTrelloCardUpperRightInformation
            logisticServiceData={logisticServiceData}
            openDelayedModalCallback={openDelayedModalCallback}
            specialNotifications={specialNotifications}
          />
        </div>

        <div className="middleSection">
          <LogisticsOriginAndDestination origin={origin} destination={destination} />
        </div>

        <div className={`bottomSection ${isPaymentAgreementWithCreditBord ? 'reverseOrder' : ''}`}>
          <div className="numberOfTools">{numberOfToolsText}</div>
          {displayBordReponsibleUserPictures ? (
            <BordResponsibleUserPictures responsibleUsersList={responsibleUsersList} dropdownDirection="left" />
          ) : (
            <LogisticServicePaymentInformation
              id={id}
              paymentData={payments}
              status={status}
              quoteDocumentUrl={quoteDocumentUrl}
              openPaymentLinkModalCallback={openPaymentLinkModal}
              proofOfPaymentUrl={proofOfPaymentUrl}
              manualQuote={manualQuote}
              quotesLog={quotesLog}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LogisticsServiceTrelloCard;
